import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getHabits, getLogs, toggleHabitLog, isHabitCompletedOnDate } from '../lib/storage';
import * as Icons from 'lucide-react';

const CalendarView = () => {
  const [view, setView] = useState('monthly');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [habits, setHabits] = useState([]);
  const [logs, setLogs] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);

  // Helper function to format date in local timezone (YYYY-MM-DD)
  const formatDateLocal = (date) => {
    if (typeof date === 'string') return date;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    loadData();
  }, [refreshKey]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [loadedHabits, loadedLogs] = await Promise.all([
        getHabits(),
        getLogs()
      ]);
      setHabits(loadedHabits || []);
      setLogs(loadedLogs || []);
    } catch (error) {
      console.error('Error loading calendar data:', error);
      setHabits([]);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getWeekDays = (date) => {
    const days = [];
    const currentDay = new Date(date);
    currentDay.setDate(currentDay.getDate() - currentDay.getDay()); // Start from Sunday

    for (let i = 0; i < 7; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }

    return days;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction * 7);
    setCurrentDate(newDate);
  };

  const handleDayClick = async (date, habitId) => {
    try {
      const dateStr = formatDateLocal(date);
      await toggleHabitLog(habitId, dateStr);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error toggling habit log:', error);
    }
  };

  const getCompletionIntensity = (date, habitId) => {
    const dateStr = formatDateLocal(date);
    const isCompleted = logs.some(log => log.habitId === habitId && log.date === dateStr);
    return isCompleted ? 'bg-success' : 'bg-stone-100 dark:bg-stone-700';
  };

  const MonthlyView = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
    const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div className="space-y-6" data-testid="monthly-view">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-2xl font-semibold text-stone-800 dark:text-stone-100">
            {monthName}
          </h3>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateMonth(-1)}
              data-testid="prev-month-button"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateMonth(1)}
              data-testid="next-month-button"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {habits.length === 0 ? (
          <div className="text-center py-12 text-stone-500 dark:text-stone-400">
            No habits to display. Add a habit to start tracking!
          </div>
        ) : (
          <div className="space-y-6">
            {habits.map((habit) => {
              const IconComponent = Icons[habit.icon] || Icons.Circle;
              
              return (
                <div
                  key={habit.id}
                  className="bg-white dark:bg-stone-800 rounded-lg border border-stone-200 dark:border-stone-700 p-4"
                  data-testid={`calendar-habit-${habit.id}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${habit.color}20` }}
                    >
                      <IconComponent className="w-4 h-4" style={{ color: habit.color }} />
                    </div>
                    <h4 className="font-medium text-stone-800 dark:text-stone-100">{habit.name}</h4>
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-2">
                    {/* Week day headers */}
                    {weekDays.map((day) => (
                      <div key={day} className="text-xs text-center text-stone-500 dark:text-stone-400 py-1">
                        {day}
                      </div>
                    ))}

                    {/* Empty cells for days before month starts */}
                    {Array.from({ length: startingDayOfWeek }).map((_, index) => (
                      <div key={`empty-${index}`} className="aspect-square" />
                    ))}

                    {/* Days of the month */}
                    {Array.from({ length: daysInMonth }).map((_, index) => {
                      const day = index + 1;
                      const date = new Date(year, month, day);
                      const dateStr = formatDateLocal(date);
                      const isToday =
                        date.toDateString() === new Date().toDateString();
                      const intensity = getCompletionIntensity(dateStr, habit.id);

                      return (
                        <button
                          key={day}
                          onClick={() => handleDayClick(dateStr, habit.id)}
                          data-testid={`calendar-day-${habit.id}-${day}`}
                          className={`aspect-square rounded-lg text-xs font-medium transition-all duration-200 hover:scale-110 ${intensity} ${
                            isToday ? 'ring-2 ring-sage' : ''
                          }`}
                        >
                          <span className={intensity === 'bg-success' ? 'text-white' : 'text-stone-600 dark:text-stone-300'}>
                            {day}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const WeeklyView = () => {
    const weekDays = getWeekDays(currentDate);
    const weekRange = `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    return (
      <div className="space-y-6" data-testid="weekly-view">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-2xl font-semibold text-stone-800 dark:text-stone-100">
            {weekRange}
          </h3>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateWeek(-1)}
              data-testid="prev-week-button"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateWeek(1)}
              data-testid="next-week-button"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {habits.length === 0 ? (
          <div className="text-center py-12 text-stone-500 dark:text-stone-400">
            No habits to display. Add a habit to start tracking!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-full bg-white dark:bg-stone-800 rounded-lg border border-stone-200 dark:border-stone-700">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200 dark:border-stone-700">
                    <th className="p-4 text-left text-sm font-medium text-stone-600 dark:text-stone-300">
                      Habit
                    </th>
                    {weekDays.map((day, index) => {
                      const isToday = day.toDateString() === new Date().toDateString();
                      return (
                        <th
                          key={index}
                          className={`p-4 text-center text-sm font-medium ${
                            isToday
                              ? 'text-sage font-semibold'
                              : 'text-stone-600 dark:text-stone-300'
                          }`}
                        >
                          <div>{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                          <div className="text-xs">{day.getDate()}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {habits.map((habit) => {
                    const IconComponent = Icons[habit.icon] || Icons.Circle;
                    
                    return (
                      <tr
                        key={habit.id}
                        className="border-b border-stone-200 dark:border-stone-700 last:border-0"
                        data-testid={`weekly-habit-${habit.id}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${habit.color}20` }}
                            >
                              <IconComponent className="w-4 h-4" style={{ color: habit.color }} />
                            </div>
                            <span className="font-medium text-stone-800 dark:text-stone-100">
                              {habit.name}
                            </span>
                          </div>
                        </td>
                        {weekDays.map((day, index) => {
                          const dateStr = formatDateLocal(day);
                          const isCompleted = logs.some(log => log.habitId === habit.id && log.date === dateStr);
                          
                          return (
                            <td key={index} className="p-4 text-center">
                              <button
                                onClick={() => handleDayClick(dateStr, habit.id)}
                                data-testid={`weekly-day-${habit.id}-${index}`}
                                className={`w-8 h-8 rounded-lg transition-all duration-200 hover:scale-110 ${
                                  isCompleted
                                    ? 'bg-success'
                                    : 'bg-stone-100 dark:bg-stone-700 hover:bg-stone-200 dark:hover:bg-stone-600'
                                }`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <h2 className="font-heading text-4xl md:text-5xl font-bold gradient-text">
        Calendar
      </h2>

      <Tabs value={view} onValueChange={setView} data-testid="calendar-view-tabs">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly</TabsTrigger>
        </TabsList>

        <TabsContent value="monthly" className="mt-6">
          <MonthlyView />
        </TabsContent>

        <TabsContent value="weekly" className="mt-6">
          <WeeklyView />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CalendarView;
